import {
  type EmployeeGoal,
  type EmployeeProfile,
} from '../../../shared/api/employee.service';
import { GOAL_AMOUNT_MIN, hasRenderableAvatarUrl } from '../../../shared/model';

import {
  IMPLEMENTED_REGISTRATION_STEP_IDS,
  REGISTRATION_STEPS,
  type RegistrationStep,
  type RegistrationStepId,
} from './registration-flow.constants';

function resolveStepPath(step: RegistrationStep): string {
  if (step.route === '/registration/onboarding' && step.onboardingSlide) {
    return `/registration/onboarding?slide=${step.onboardingSlide}`;
  }

  return step.route;
}

export function getNextRegistrationStep(
  stepId: RegistrationStepId,
): RegistrationStep | null {
  const currentStepIndex = REGISTRATION_STEPS.findIndex(
    step => step.id === stepId,
  );
  const nextStepIndex = currentStepIndex + 1;

  if (currentStepIndex < 0 || nextStepIndex >= REGISTRATION_STEPS.length) {
    return null;
  }

  return REGISTRATION_STEPS[nextStepIndex];
}

export function resolveNextRegistrationPath(
  stepId: RegistrationStepId,
): string | null {
  const nextStep = getNextRegistrationStep(stepId);

  return nextStep ? resolveStepPath(nextStep) : null;
}

export function resolveNextImplementedRegistrationPath(
  stepId: RegistrationStepId,
): string | null {
  const currentStepIndex = REGISTRATION_STEPS.findIndex(
    step => step.id === stepId,
  );

  if (currentStepIndex < 0) {
    return null;
  }

  const nextStep = REGISTRATION_STEPS.slice(currentStepIndex + 1).find(step =>
    IMPLEMENTED_REGISTRATION_STEP_IDS.includes(step.id),
  );

  return nextStep ? resolveStepPath(nextStep) : null;
}

export function resolvePreviousImplementedRegistrationPath(
  stepId: RegistrationStepId,
): string | null {
  const currentStepIndex = REGISTRATION_STEPS.findIndex(
    step => step.id === stepId,
  );

  if (currentStepIndex <= 0) {
    return null;
  }

  const previousStep = [...REGISTRATION_STEPS]
    .slice(0, currentStepIndex)
    .reverse()
    .find(step => IMPLEMENTED_REGISTRATION_STEP_IDS.includes(step.id));

  return previousStep ? resolveStepPath(previousStep) : null;
}

function hasCompletedPersonalData(profile: EmployeeProfile): boolean {
  return (
    (profile.firstName?.trim().length ?? 0) > 0 &&
    (profile.lastName?.trim().length ?? 0) > 0 &&
    (profile.tin?.trim().length ?? 0) > 0
  );
}

function hasCompletedEmail(profile: EmployeeProfile): boolean {
  return (profile.email?.trim().length ?? 0) > 0;
}

function hasCompletedGoal(goal: EmployeeGoal | null): boolean {
  return (goal?.text.trim().length ?? 0) > 0;
}

function hasCompletedGoalValue(goal: EmployeeGoal | null): boolean {
  return goal !== null && goal.targetAmount >= GOAL_AMOUNT_MIN;
}

function hasCompletedPhoto(profile: EmployeeProfile): boolean {
  return hasRenderableAvatarUrl(profile.avatarUrl);
}

function hasStartedPostIntroRegistration(
  profile: EmployeeProfile,
  goal: EmployeeGoal | null,
): boolean {
  return (
    hasCompletedPersonalData(profile) ||
    hasCompletedPhoto(profile) ||
    hasCompletedGoal(goal) ||
    hasCompletedGoalValue(goal)
  );
}

export function resolveRegistrationResumePath(
  profile: EmployeeProfile,
  goal: EmployeeGoal | null,
): string {
  if (!hasCompletedEmail(profile)) {
    return hasStartedPostIntroRegistration(profile, goal)
      ? '/registration/email'
      : '/registration/onboarding?slide=welcome';
  }

  if (!hasCompletedPersonalData(profile)) {
    return '/registration/personal-data';
  }

  if (!hasCompletedPhoto(profile)) {
    return '/registration/photo';
  }

  if (!hasCompletedGoal(goal)) {
    return '/registration/goal';
  }

  if (!hasCompletedGoalValue(goal)) {
    return '/registration/value';
  }

  return '/registration/congrats?state=card';
}
